// components/MicrophoneInput.tsx
import React, { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { Theme } from "../types/theme";
import chatService from "../api/chatService";

interface MicrophoneInputProps {
  theme: Theme;
  onSpeechRecognized?: (text: string) => void;
  isListening?: boolean;
  onListeningChange?: (listening: boolean) => void;
  disabled?: boolean;
}

const MicrophoneInput: React.FC<MicrophoneInputProps> = ({
  theme,
  onSpeechRecognized,
  isListening,
  onListeningChange,
  disabled = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunks.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsLoading(true);
        const audioBlob = new Blob(chunks.current, { type: "audio/webm" });

        try {
          const result = await chatService.transcribeAudio(audioBlob);

          // ✅ use "text" instead of "transcript"
          if (result?.transcript) {
            onSpeechRecognized?.(result.transcript);
          }
        } catch (err) {
          console.error("Transcription error:", err);
        } finally {
          setIsLoading(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      onListeningChange?.(true);
    } catch (err) {
      console.error("Microphone error:", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      onListeningChange?.(false);
    }
  };

  const toggleRecording = () => {
    if (disabled) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <button
      onClick={toggleRecording}
      disabled={disabled || isLoading}
      className={`p-2 rounded-lg transition-all duration-200 hover:opacity-80 disabled:opacity-50 ${isRecording ? "animate-pulse" : ""
        }`}
      style={{
        color: isRecording ? "#ef4444" : theme.textSecondaryColor,
        backgroundColor: `${theme.surfaceColor}80`,
      }}
      title={isRecording ? "Stop recording" : "Start voice input"}
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : isRecording ? (
        <Square className="w-5 h-5" />
      ) : (
        <Mic className="w-5 h-5" />
      )}
    </button>
  );
};

export default MicrophoneInput;
